import React from 'react';
import './SupplierDetailsCard.css';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonItem,
  IonLabel,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonBadge,
  IonNote,
} from '@ionic/react';
import {
  personOutline,
  mailOutline,
  callOutline,
  businessOutline,
  cardOutline,
  globeOutline,
  walletOutline,
  documentTextOutline,
} from 'ionicons/icons';
import { Supplier } from '../types';
import {
  getCurrencySymbol,
  DEFAULT_CURRENCY,
} from '../utils/currencyConstants';

interface SupplierDetailsCardProps {
  supplier: Supplier | null;
  loading?: boolean;
  compact?: boolean;
}

const SupplierDetailsCard: React.FC<SupplierDetailsCardProps> = ({
  supplier,
  loading,
  compact = false,
}) => {
  if (loading) {
    return (
      <IonCard>
        <IonCardContent>
          <p className="text-center">Loading supplier details...</p>
        </IonCardContent>
      </IonCard>
    );
  }

  if (!supplier) {
    return null;
  }

  const formatBalance = (balance: number) => {
    const absBalance = Math.abs(balance);
    const currency = getCurrencySymbol(DEFAULT_CURRENCY);
    const amount = `${currency}${absBalance.toFixed(2)}`;

    if (balance > 0) {
      return { text: `You owe: ${amount}`, color: 'danger' };
    } else if (balance < 0) {
      return { text: `They owe: ${amount}`, color: 'success' };
    } else {
      return { text: 'No balance', color: 'medium' };
    }
  };

  const balanceInfo = formatBalance(supplier.calculatedBalance || 0);

  return (
    <IonCard className={`supplier-details-card ${compact ? 'compact' : ''}`}>
      {!compact && (
        <IonCardHeader>
          <IonCardTitle>Supplier Details</IonCardTitle>
        </IonCardHeader>
      )}
      <IonCardContent className={compact ? 'ion-no-padding' : ''}>
        {compact ? (
          // Compact horizontal layout for AddSaleModal
          <div className="px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <IonIcon icon={personOutline} color="primary" />
                <span className="font-medium">{supplier.name}</span>
              </div>
              <span
                className={`inline-flex items-center px-2 py-1 rounded-md text-white text-xs font-semibold ${
                  balanceInfo.color === 'danger'
                    ? 'bg-red-500'
                    : balanceInfo.color === 'success'
                    ? 'bg-green-500'
                    : 'bg-gray-500'
                }`}
              >
                {balanceInfo.text}
              </span>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
              {supplier.email && (
                <div className="flex items-center gap-1">
                  <IonIcon icon={mailOutline} className="text-base" />
                  <span>{supplier.email}</span>
                </div>
              )}
              {supplier.phone && (
                <div className="flex items-center gap-1">
                  <IonIcon icon={callOutline} className="text-base" />
                  <span>{supplier.phone}</span>
                </div>
              )}
              {supplier.paypalEmail && (
                <div className="flex items-center gap-1">
                  <IonIcon icon={globeOutline} className="text-base" />
                  <span>PayPal: {supplier.paypalEmail}</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Original full layout
          <IonGrid>
            {/* Basic Information */}
            <IonRow>
              <IonCol size="12">
                <IonItem lines="none">
                  <IonIcon icon={personOutline} slot="start" color="primary" />
                  <IonLabel>
                    <p>Name</p>
                    <h3>{supplier.name}</h3>
                  </IonLabel>
                </IonItem>
              </IonCol>
            </IonRow>

            {/* Balance - Full width for better visibility */}
            <IonRow>
              <IonCol size="12">
                <IonItem lines="none">
                  <IonIcon
                    icon={walletOutline}
                    slot="start"
                    color={balanceInfo.color}
                  />
                  <IonLabel>
                    <p>Balance</p>
                    <h3 className="balance-display">
                      <span
                        className={`inline-flex items-center px-3 py-1.5 rounded-md text-white text-sm font-semibold ${
                          balanceInfo.color === 'danger'
                            ? 'bg-red-500'
                            : balanceInfo.color === 'success'
                            ? 'bg-green-500'
                            : 'bg-gray-500'
                        }`}
                      >
                        {balanceInfo.text}
                      </span>
                    </h3>
                  </IonLabel>
                </IonItem>
              </IonCol>
            </IonRow>

            {/* Contact Information */}
            {(supplier.email || supplier.phone || supplier.company) && (
              <>
                <IonRow className="mt-3">
                  <IonCol size="12">
                    <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">
                      Contact Information
                    </h4>
                  </IonCol>
                </IonRow>
                <IonRow className="flex flex-col gap-2">
                  {supplier.email && (
                    <IonCol
                      size="12"
                      sizeMd="4"
                      className="!w-full !max-w-none"
                    >
                      <IonItem lines="none">
                        <IonIcon
                          icon={mailOutline}
                          slot="start"
                          color="medium"
                        />
                        <IonLabel>
                          <p>Email</p>
                          <h3 className="text-sm">{supplier.email}</h3>
                        </IonLabel>
                      </IonItem>
                    </IonCol>
                  )}
                  {supplier.phone && (
                    <IonCol
                      size="12"
                      sizeMd="4"
                      className="!w-full !max-w-none"
                    >
                      <IonItem lines="none">
                        <IonIcon
                          icon={callOutline}
                          slot="start"
                          color="medium"
                        />
                        <IonLabel>
                          <p>Phone</p>
                          <h3 className="text-sm">{supplier.phone}</h3>
                        </IonLabel>
                      </IonItem>
                    </IonCol>
                  )}
                  {supplier.company && (
                    <IonCol
                      size="12"
                      sizeMd="4"
                      className="!w-full !max-w-none"
                    >
                      <IonItem lines="none">
                        <IonIcon
                          icon={businessOutline}
                          slot="start"
                          color="medium"
                        />
                        <IonLabel>
                          <p>Company</p>
                          <h3 className="text-sm">{supplier.company}</h3>
                        </IonLabel>
                      </IonItem>
                    </IonCol>
                  )}
                </IonRow>
              </>
            )}

            {/* Payment Methods */}
            {(supplier.bankName ||
              supplier.accountNumber ||
              supplier.iban ||
              supplier.paypalEmail) && (
              <>
                <IonRow className="mt-3">
                  <IonCol size="12">
                    <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">
                      Payment Methods
                    </h4>
                  </IonCol>
                </IonRow>
                <IonRow>
                  {(supplier.bankName ||
                    supplier.accountNumber ||
                    supplier.iban) && (
                    <IonCol size="12" sizeMd="6">
                      <IonItem lines="none">
                        <IonIcon
                          icon={cardOutline}
                          slot="start"
                          color="medium"
                        />
                        <IonLabel>
                          <p>Bank Details</p>
                          {supplier.bankName && (
                            <h3 className="text-sm">
                              Bank: {supplier.bankName}
                            </h3>
                          )}
                          {supplier.accountName && (
                            <h3 className="text-sm">
                              Account Name: {supplier.accountName}
                            </h3>
                          )}
                          {supplier.accountNumber && (
                            <h3 className="text-sm">
                              Account: ***{supplier.accountNumber.slice(-4)}
                            </h3>
                          )}
                          {supplier.sortCode && (
                            <h3 className="text-sm">
                              Sort Code: {supplier.sortCode}
                            </h3>
                          )}
                          {supplier.iban && (
                            <h3 className="text-sm">IBAN: {supplier.iban}</h3>
                          )}
                          {supplier.swiftCode && (
                            <h3 className="text-sm">
                              SWIFT: {supplier.swiftCode}
                            </h3>
                          )}
                          {supplier.bankCountry && (
                            <h3 className="text-sm">
                              Country: {supplier.bankCountry}
                            </h3>
                          )}
                          {supplier.accountType && (
                            <h3 className="text-sm">
                              Type: {supplier.accountType}
                            </h3>
                          )}
                        </IonLabel>
                      </IonItem>
                    </IonCol>
                  )}
                  {supplier.paypalEmail && (
                    <IonCol size="12" sizeMd="6">
                      <IonItem lines="none">
                        <IonIcon
                          icon={globeOutline}
                          slot="start"
                          color="medium"
                        />
                        <IonLabel>
                          <p>PayPal</p>
                          <h3 className="text-sm">{supplier.paypalEmail}</h3>
                        </IonLabel>
                      </IonItem>
                    </IonCol>
                  )}
                </IonRow>
              </>
            )}

            {/* Notes */}
            {supplier.notes && (
              <IonRow className="mt-3">
                <IonCol size="12">
                  <IonItem lines="none">
                    <IonIcon
                      icon={documentTextOutline}
                      slot="start"
                      color="medium"
                    />
                    <IonLabel>
                      <p>Notes</p>
                      <h3 className="text-sm">{supplier.notes}</h3>
                    </IonLabel>
                  </IonItem>
                </IonCol>
              </IonRow>
            )}

            {/* Stats if available */}
            {(supplier.totalFromSales !== undefined ||
              supplier.salesCount !== undefined) && (
              <IonRow className="mt-3">
                <IonCol size="12">
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 pt-3 border-t">
                    {supplier.salesCount !== undefined && (
                      <span>Total Sales: {supplier.salesCount}</span>
                    )}
                    {supplier.totalFromSales !== undefined && (
                      <span>
                        Sales Value: {getCurrencySymbol(DEFAULT_CURRENCY)}
                        {supplier.totalFromSales.toFixed(2)}
                      </span>
                    )}
                  </div>
                </IonCol>
              </IonRow>
            )}
          </IonGrid>
        )}
      </IonCardContent>
    </IonCard>
  );
};

export default SupplierDetailsCard;
